import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from "@angular/core";
import {Order, Product} from "../../../../store/state/order.state";
import {OutsourcedProcessFormService} from "./outsourced-process-form.service";
import {FormGroup} from "@angular/forms";
import {filter, map, takeWhile} from "rxjs/operators";
import {Observable} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {BaseSubFormComponent} from "../../../../umbrella-common/components/base-sub-form.component";
import {TranslationsHelperService} from "../../../translations/translations-helper.service";

@Component({
    selector: 'outsourced-process',
    templateUrl: './outsourced-process.template.html',
    styleUrls: ['./outsourced-process.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OutsourcedProcessComponent extends BaseSubFormComponent implements OnInit, OnDestroy {

	readonly OUTSOURCED_PROCESSES_KEY_BASE = "additional-questions.outsources-processes.";
	readonly predefinedOutsourcedProcessesKeys: string[] = ['none', 'design', 'manufacturing', 'sales'];

	@Input() order: Order;
	@Input() product: Product;
	@Input() blocked: boolean = false;

	form: FormGroup;
	customProcessCheckboxSelected: boolean;

	private alive: boolean;

	constructor(public formService: OutsourcedProcessFormService, private changeDet: ChangeDetectorRef, private translate: TranslateService, private translationsHelper: TranslationsHelperService) {
		super(formService);
	}

	ngOnInit(): void {
		this.alive = true;
		this.form = this.formService.buildSubForm(this.product.standard.name, this.totalSubForms(this.order), this.product, this.predefinedOutsourcedProcessesKeys);
		this.customProcessCheckboxSelected = this.hasCustomOutsourcedProcess(this.predefinedOutsourcedProcessesKeys);
		this.formService.customProcessCheckboxSelected$().pipe(takeWhile(() => this.alive), filter(event => event.formKey == this.product.standard.name))
			.subscribe(event => {
				this.customProcessCheckboxSelected = event.checked;
				this.changeDet.detectChanges();
			});
	}

	predefinedOutsourcedProcesses(): Observable<string[]> {
		return this.translate.get(this.predefinedOutsourcedProcessesKeys).pipe(takeWhile(() => this.alive), map(translations => {
			return this.predefinedOutsourcedProcessesKeys.map(key => translations[key]);
		}));
	}

	override ngOnDestroy(): void {
		this.alive = false;
		this.formService.destroySubForm(this.product.standard.name);
	}

	isSelected(outsourcedProcess: string): boolean {
		return this.product.additionalQuestions.outsourcedProcesses.includes(outsourcedProcess);
	}

	getCustomProcessTextAreaControlName(): string {
		return `outsourced_process_${this.predefinedOutsourcedProcessesKeys.length}${this.product.standard.name}`;
	}

	getRadioControlName(processIndex: number) {
		return `outsourced_process_${processIndex}${this.product.standard.name}`;
	}

	// needed for angular to not rebuild component added by *ngFor
	trackByFcn(index: number) {
		return index;
	}

	shouldDisplayValidationMsgForCheckboxes(): Observable<boolean> {
		return this.predefinedOutsourcedProcesses().pipe(map(op => this.formService.shouldDisplayValidationMsgForCheckboxes(this.product, op)));
	}

	shouldDisableControl(controlName: string): boolean {
		return this.formService.getFormControl(this.product.standard.name, controlName).disabled;
	}

	customOutsourcedProcessChecked(): boolean {
		return this.hasCustomOutsourcedProcess(this.predefinedOutsourcedProcessesKeys);
	}

	getTranslationKey(process): string {
		return this.OUTSOURCED_PROCESSES_KEY_BASE + process;
	}

	getFormKey(): string {
		return this.product.standard.name;
	}

	protected iso27701HasItsOwnSubForm(): boolean {
		return false;
	}

	private hasCustomOutsourcedProcess(predefinedOutsourcedProcesses: string[]): boolean {
		return this.customProcessCheckboxSelected || this.product.additionalQuestions.outsourcedProcesses.some(selectedProcess => !predefinedOutsourcedProcesses.includes(selectedProcess));
	}

	translationExists(): boolean {
		return this.translationsHelper.translationExists(this.product.standard.name, 'additionalQuestions.customOutsourcedProcess', false);
	}

	translatedOutsourcedProcess(): string {
		return this.translationsHelper.getTranslation(this.product.standard.name, 'additionalQuestions.customOutsourcedProcess', false);
	}
}
